import { useState, useRef, ReactNode, FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Sparkles, X, Undo2 } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Button } from '@/components/ui/button'
import {
  Field, FieldGroup, FieldLabel, FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  InlineText, InlineNumber, InlineSelect, InlineDate, Dash, extractErr,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'
import GenerateDocumentButton from '../components/GenerateDocumentButton'
import GeneratedDocumentsList from '../components/GeneratedDocumentsList'

type Profile = {
  id: number
  person: number
  person_name: string
  anonymous_code: string
  headline: string
  profile_summary: string
  total_years_experience: number
  industries: string[]
  highest_education_level: string
  education_institution: string
  notable_projects_text: string
  is_active: boolean
  skills: ProfileSkill[]
  languages: ProfileLanguage[]
  certifications: ProfileCertification[]
  experiences: ProfileExperience[]
}

type ProfileExperience = {
  id: number
  profile: number
  role: string
  employer_anonymized: string
  industry: string
  start_date: string | null
  end_date: string | null
  is_current: boolean
  scope_summary: string
  technologies: number[]
  technology_names: string[]
  sort_order: number
}

type Skill = {
  id: number
  name: string
  category: string
}

type ProfileSkill = {
  id: number
  profile: number
  skill: number
  skill_name: string
  skill_category: string
  level: 'JUNIOR' | 'MID' | 'SENIOR' | 'EXPERT'
  years_experience: number
  last_used_date: string | null
}

type ProfileLanguage = {
  id: number
  profile: number
  language_code: string
  level: string
}

type ProfileCertification = {
  id: number
  profile: number
  name: string
  issuing_org: string
  valid_from: string | null
  valid_until: string | null
}

type TabKey = 'overview' | 'experience' | 'skills' | 'languages' | 'certifications' | 'generated'

const EDUCATION_OPTIONS = [
  { value: 'HS', label: 'High school' },
  { value: 'BSC', label: 'Bachelor' },
  { value: 'MSC', label: 'Master' },
  { value: 'PHD', label: 'PhD' },
  { value: 'MBA', label: 'MBA' },
  { value: 'OTHER', label: 'Other' },
]

const LEVEL_OPTIONS = [
  { value: 'JUNIOR', label: 'Junior' },
  { value: 'MID', label: 'Mid' },
  { value: 'SENIOR', label: 'Senior' },
  { value: 'EXPERT', label: 'Expert' },
]

const LANG_LEVELS = [
  { value: 'A1', label: 'A1 — Beginner' },
  { value: 'A2', label: 'A2 — Elementary' },
  { value: 'B1', label: 'B1 — Intermediate' },
  { value: 'B2', label: 'B2 — Upper intermediate' },
  { value: 'C1', label: 'C1 — Advanced' },
  { value: 'C2', label: 'C2 — Proficient' },
  { value: 'NATIVE', label: 'Native' },
]

export default function ConsultantProfileDetail() {
  const { id } = useParams<{ id: string }>()
  const profileId = id ? Number(id) : 0
  const navigate = useNavigate()
  const qc = useQueryClient()

  const profileQ = useQuery({
    queryKey: ['consulting', 'profile', profileId],
    queryFn: async () => (await api.get<Profile>(`/consulting/profiles/${profileId}/`)).data,
    enabled: !!profileId,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Profile>) =>
      (await api.patch(`/consulting/profiles/${profileId}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'profile', profileId] }),
  })

  const [tab, setTab] = useState<TabKey>('overview')

  // Parse-CV state
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [parsing, setParsing] = useState(false)
  const [parseBanner, setParseBanner] = useState<{
    operationId: string
    created: { experiences: number; skills: number; languages: number; certifications: number }
  } | null>(null)

  const parseCvMut = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('cv', file)
      const r = await api.post(`/consulting/profiles/${profileId}/parse-cv/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return r.data as { operation_id: string; created: { experiences: number; skills: number; languages: number; certifications: number } }
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['consulting', 'profile', profileId] })
      setParseBanner({ operationId: d.operation_id, created: d.created })
      setParsing(false)
      toast.success('CV parsed and applied')
    },
    onError: (e) => {
      toast.error(extractErr(e))
      setParsing(false)
    },
  })

  const undoMut = useMutation({
    mutationFn: async (opId: string) =>
      (await api.post(`/consulting/profiles/${profileId}/parse-cv/undo/${opId}/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'profile', profileId] })
      setParseBanner(null)
      toast.success('Parse-CV reverted')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const triggerCvUpload = () => {
    fileInputRef.current?.click()
  }
  const onCvFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    setParsing(true)
    parseCvMut.mutate(f)
    // Reset so picking the same filename again still triggers change
    e.target.value = ''
  }

  if (profileQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (profileQ.isError || !profileQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Profile not found.</p>
          <Link to="/admin/consulting/consultants" className="text-blue-600 hover:underline">← Back to consultants</Link>
        </div>
      </AdminLayout>
    )
  }

  const profile = profileQ.data
  const save = (payload: Partial<Profile>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/consulting/consultants" className="text-muted-foreground hover:text-foreground hover:underline">← Profiles</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div>
            <h2 className="text-2xl font-bold text-foreground font-mono">{profile.anonymous_code}</h2>
            <div className="text-sm text-muted-foreground">
              {profile.person_name} <span className="text-xs">· internal only</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt"
              className="hidden"
              onChange={onCvFileChosen}
            />
            <Button variant="outline" onClick={triggerCvUpload} disabled={parsing}>
              <Sparkles className="h-4 w-4 mr-1.5" />
              {parsing ? 'Parsing…' : 'Parse CV'}
            </Button>
            <GenerateDocumentButton
              sourceType="consulting.ConsultantProfile"
              sourceId={profile.id}
              label="Generate Profile"
            />
          </div>
        </div>
      </div>

      {/* Parse-CV success banner with Undo */}
      {parseBanner && (
        <div className="mb-4 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-emerald-700 shrink-0" />
          <div className="flex-1 text-sm text-emerald-900">
            <span className="font-semibold">CV parsed.</span>{' '}
            Added{' '}
            {parseBanner.created.experiences} experience{parseBanner.created.experiences === 1 ? '' : 's'},{' '}
            {parseBanner.created.skills} skill{parseBanner.created.skills === 1 ? '' : 's'},{' '}
            {parseBanner.created.languages} language{parseBanner.created.languages === 1 ? '' : 's'},{' '}
            {parseBanner.created.certifications} certification{parseBanner.created.certifications === 1 ? '' : 's'}.
          </div>
          <Button
            variant="outline" size="sm"
            onClick={() => undoMut.mutate(parseBanner.operationId)}
            disabled={undoMut.isPending}
          >
            <Undo2 className="h-3.5 w-3.5 mr-1" />
            {undoMut.isPending ? 'Undoing…' : 'Undo'}
          </Button>
          <button
            type="button"
            onClick={() => setParseBanner(null)}
            className="text-emerald-700 hover:text-emerald-900"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['overview', 'experience', 'skills', 'languages', 'certifications', 'generated'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'overview' && 'Overview'}
              {t === 'experience' && `Experience (${profile.experiences.length})`}
              {t === 'skills' && `Skills (${profile.skills.length})`}
              {t === 'languages' && `Languages (${profile.languages.length})`}
              {t === 'certifications' && `Certifications (${profile.certifications.length})`}
              {t === 'generated' && 'Generated'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'overview' && <OverviewTab profile={profile} onSave={save} />}
      {tab === 'experience' && <ExperienceTab profile={profile} qc={qc} />}
      {tab === 'skills' && <SkillsTab profile={profile} qc={qc} />}
      {tab === 'languages' && <LanguagesTab profile={profile} qc={qc} />}
      {tab === 'certifications' && <CertificationsTab profile={profile} qc={qc} />}
      {tab === 'generated' && (
        <GeneratedDocumentsList
          sourceType="consulting.ConsultantProfile"
          sourceId={profile.id}
        />
      )}
    </AdminLayout>
  )
}

// ─────────────── Overview ───────────────

function OverviewTab({ profile, onSave }: { profile: Profile; onSave: (p: Partial<Profile>) => Promise<void> }) {
  return (
    <div className="space-y-6">
      <Section title="Profile">
        <Row label="Headline">
          <InlineText
            value={profile.headline}
            onSave={(v) => onSave({ headline: v })}
            placeholder="e.g. Senior Developer, Business Analyst, QA Engineer"
          />
        </Row>
      </Section>

      <Section title="Pitch">
        <Row label="Summary">
          <InlineText
            value={profile.profile_summary}
            multiline
            onSave={(v) => onSave({ profile_summary: v })}
            placeholder="3–5 sentence elevator pitch"
          />
        </Row>
      </Section>

      <Section title="Experience">
        <Row label="Total years">
          <InlineNumber
            value={String(profile.total_years_experience)}
            onSave={(v) => onSave({ total_years_experience: Number(v || 0) })}
          />
        </Row>
        <Row label="Highest education">
          <InlineSelect
            value={profile.highest_education_level}
            allowClear
            options={EDUCATION_OPTIONS}
            onSave={(v) => onSave({ highest_education_level: v as any })}
            placeholder="—"
          />
        </Row>
        <Row label="Issued by">
          <InlineText
            value={profile.education_institution}
            onSave={(v) => onSave({ education_institution: v })}
            placeholder="e.g. Politehnica Bucharest, ETH Zurich"
          />
        </Row>
        <Row label="Industries">
          <InlineText
            value={(profile.industries || []).join(', ')}
            onSave={(v) =>
              onSave({ industries: v.split(',').map((s) => s.trim()).filter(Boolean) })
            }
            placeholder="Banking, Insurance, Telco"
          />
        </Row>
      </Section>

      <Section title="Notable projects (text)">
        <Row label="One per line">
          <InlineText
            value={profile.notable_projects_text}
            multiline
            onSave={(v) => onSave({ notable_projects_text: v })}
            placeholder="One sanitized project per line — domain, role, scope, tech"
          />
        </Row>
      </Section>
    </div>
  )
}

// ─────────────── Experience tab ───────────────

function ExperienceTab({ profile, qc }: { profile: Profile; qc: ReturnType<typeof useQueryClient> }) {
  const skillsCatalogQ = useQuery({
    queryKey: ['consulting', 'skills'],
    queryFn: async () => {
      const r = await api.get('/consulting/skills/')
      const body = r.data
      if (Array.isArray(body)) return body as Skill[]
      if (body && Array.isArray(body.results)) return body.results as Skill[]
      return [] as Skill[]
    },
  })
  const catalog = skillsCatalogQ.data || []
  const skillName = (id: number) => catalog.find((s) => s.id === id)?.name || `#${id}`

  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<ProfileExperience | null>(null)

  const upsertMut = useMutation({
    mutationFn: async (payload: any) => {
      if (payload.id) {
        const { id, ...rest } = payload
        return (await api.patch(`/consulting/profile-experiences/${id}/`, rest)).data
      }
      return (await api.post('/consulting/profile-experiences/', payload)).data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'profile', profile.id] })
      setAdding(false)
      setEditing(null)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const delMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/consulting/profile-experiences/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'profile', profile.id] }),
  })

  return (
    <div>
      <div className="flex justify-end mb-3"><Button onClick={() => setAdding(true)}>+ Add experience</Button></div>

      {profile.experiences.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No past experience listed yet. Use the Parse CV button on the header to populate from a CV, or add manually.
        </div>
      ) : (
        <div className="space-y-3">
          {profile.experiences.map((x) => (
            <div key={x.id} className="bg-card rounded-lg shadow p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="font-medium">
                    {x.role}
                    {x.employer_anonymized && <span className="text-muted-foreground"> · {x.employer_anonymized}</span>}
                    {x.industry && <span className="text-muted-foreground text-sm"> · {x.industry}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground italic mt-0.5">
                    {x.is_current
                      ? `${x.start_date || '?'} — present`
                      : x.start_date && x.end_date
                        ? `${x.start_date} — ${x.end_date}`
                        : x.start_date
                          ? `from ${x.start_date}`
                          : <Dash />}
                  </div>
                  {x.scope_summary && <p className="text-sm mt-2 whitespace-pre-wrap">{x.scope_summary}</p>}
                  {x.technology_names.length > 0 && (
                    <div className="text-xs text-muted-foreground mt-2">
                      Tech: <span className="italic">{x.technology_names.join(', ')}</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0 ml-4">
                  <button className="text-blue-600 hover:underline text-sm" onClick={() => setEditing(x)}>Edit</button>
                  <button
                    className="text-red-600 hover:underline text-sm"
                    onClick={() => { if (confirm(`Remove "${x.role}"?`)) delMut.mutate(x.id) }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(adding || editing) && (
        <ExperienceDialog
          initial={editing}
          catalog={catalog}
          skillName={skillName}
          profileId={profile.id}
          submitting={upsertMut.isPending}
          onCancel={() => { setAdding(false); setEditing(null) }}
          onSubmit={(payload) => upsertMut.mutate(payload)}
        />
      )}
    </div>
  )
}

function ExperienceDialog({
  initial, catalog, skillName, profileId, onSubmit, onCancel, submitting,
}: {
  initial: ProfileExperience | null
  catalog: Skill[]
  skillName: (id: number) => string
  profileId: number
  onSubmit: (payload: any) => void
  onCancel: () => void
  submitting: boolean
}) {
  const [form, setForm] = useState({
    role: initial?.role || '',
    employer_anonymized: initial?.employer_anonymized || '',
    industry: initial?.industry || '',
    start_date: (initial?.start_date || '') as string | null,
    end_date: (initial?.end_date || '') as string | null,
    is_current: initial?.is_current || false,
    scope_summary: initial?.scope_summary || '',
    technologies: initial?.technologies || [] as number[],
    sort_order: initial?.sort_order || 0,
  })
  const [techToAdd, setTechToAdd] = useState<string | undefined>(undefined)

  const toggleTech = (id: number) => {
    setForm((f) => ({
      ...f,
      technologies: f.technologies.includes(id)
        ? f.technologies.filter((x) => x !== id)
        : [...f.technologies, id],
    }))
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit({
      ...(initial ? { id: initial.id } : {}),
      profile: profileId,
      role: form.role,
      employer_anonymized: form.employer_anonymized,
      industry: form.industry,
      start_date: form.start_date || null,
      end_date: form.is_current ? null : (form.end_date || null),
      is_current: form.is_current,
      scope_summary: form.scope_summary,
      technologies: form.technologies,
      sort_order: form.sort_order,
    })
  }

  const availableForAdd = catalog.filter((s) => !form.technologies.includes(s.id))

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-40">
      <div className="bg-card rounded-lg shadow-2xl max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-3">{initial ? 'Edit experience' : 'Add experience'}</h3>
        <form onSubmit={submit}>
          <FieldSet>
            <FieldGroup>
              <Field>
                <FieldLabel>Role</FieldLabel>
                <Input required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Senior Backend Engineer" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <FieldLabel>Anonymized employer</FieldLabel>
                  <Input
                    value={form.employer_anonymized}
                    onChange={(e) => setForm({ ...form, employer_anonymized: e.target.value })}
                    placeholder="Tier-1 European bank"
                  />
                </Field>
                <Field>
                  <FieldLabel>Industry</FieldLabel>
                  <Input
                    value={form.industry}
                    onChange={(e) => setForm({ ...form, industry: e.target.value })}
                    placeholder="Banking"
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <FieldLabel>Start</FieldLabel>
                  <DatePicker
                    value={form.start_date || ''}
                    onChange={(v) => setForm({ ...form, start_date: v })}
                    captionLayout="dropdown"
                  />
                </Field>
                <Field>
                  <FieldLabel>End</FieldLabel>
                  <DatePicker
                    value={form.is_current ? '' : (form.end_date || '')}
                    onChange={(v) => setForm({ ...form, end_date: v })}
                    disabled={form.is_current}
                    captionLayout="dropdown"
                  />
                </Field>
              </div>
              <label className="flex items-center space-x-2 text-sm">
                <input type="checkbox" checked={form.is_current} onChange={(e) => setForm({ ...form, is_current: e.target.checked })} />
                <span>Current engagement (no end date)</span>
              </label>
              <Field>
                <FieldLabel>Scope / responsibilities</FieldLabel>
                <Textarea
                  rows={4}
                  value={form.scope_summary}
                  onChange={(e) => setForm({ ...form, scope_summary: e.target.value })}
                  placeholder="Sanitized — no client names. Describe scope, responsibilities, outcomes."
                />
              </Field>
              <Field>
                <FieldLabel>Technologies</FieldLabel>
                <div className="space-y-2">
                  {form.technologies.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {form.technologies.map((id) => (
                        <span key={id} className="inline-flex items-center gap-1 bg-muted/50 rounded px-2 py-0.5 text-xs">
                          {skillName(id)}
                          <button type="button" onClick={() => toggleTech(id)} className="text-muted-foreground hover:text-red-600">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <SearchableSelect
                    value={techToAdd}
                    onChange={(v) => { toggleTech(Number(v)); setTechToAdd(undefined) }}
                    options={availableForAdd.map((s) => ({ value: String(s.id), label: s.name }))}
                    placeholder="Add technology…"
                    emptyLabel="All technologies already added"
                  />
                </div>
              </Field>
            </FieldGroup>
          </FieldSet>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={!form.role || submitting}>{submitting ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────── Skills tab ───────────────

function SkillsTab({ profile, qc }: { profile: Profile; qc: ReturnType<typeof useQueryClient> }) {
  const skillsCatalogQ = useQuery({
    queryKey: ['consulting', 'skills'],
    queryFn: async () => {
      const r = await api.get('/consulting/skills/')
      const body = r.data
      if (Array.isArray(body)) return body as Skill[]
      if (body && Array.isArray(body.results)) return body.results as Skill[]
      return [] as Skill[]
    },
  })

  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState({
    skill: '',
    level: 'MID' as ProfileSkill['level'],
    years_experience: 1,
    last_used_date: '' as string | null,
  })

  const addMut = useMutation({
    mutationFn: async () =>
      (await api.post('/consulting/profile-skills/', {
        profile: profile.id,
        skill: Number(draft.skill),
        level: draft.level,
        years_experience: draft.years_experience,
        last_used_date: draft.last_used_date || null,
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'profile', profile.id] })
      setAdding(false)
      setDraft({ skill: '', level: 'MID', years_experience: 1, last_used_date: '' })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/consulting/profile-skills/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'profile', profile.id] }),
  })

  const catalog = skillsCatalogQ.data || []
  const usedIds = new Set(profile.skills.map((s) => s.skill))
  const available = catalog.filter((s) => !usedIds.has(s.id))

  // Group rendered profile.skills by category
  const grouped: Record<string, ProfileSkill[]> = {}
  profile.skills.forEach((s) => {
    if (!grouped[s.skill_category]) grouped[s.skill_category] = []
    grouped[s.skill_category].push(s)
  })

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button onClick={() => setAdding(true)} disabled={available.length === 0}>+ Add skill</Button>
      </div>
      {Object.keys(grouped).length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No skills on this profile yet.
        </div>
      ) : (
        Object.entries(grouped).map(([cat, items]) => (
          <div key={cat} className="bg-card rounded-lg shadow mb-4 overflow-hidden">
            <div className="px-4 py-2 bg-muted/30 border-b text-xs uppercase tracking-wide font-semibold text-muted-foreground">{cat}</div>
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr>
                  <th className="text-left px-4 py-2">Skill</th>
                  <th className="text-left px-4 py-2">Level</th>
                  <th className="text-right px-4 py-2">Years</th>
                  <th className="text-left px-4 py-2">Last used</th>
                  <th className="text-right px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map((s) => (
                  <tr key={s.id}>
                    <td className="px-4 py-2 font-medium">{s.skill_name}</td>
                    <td className="px-4 py-2">{s.level}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{s.years_experience}</td>
                    <td className="px-4 py-2 text-muted-foreground">{s.last_used_date || <Dash />}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        className="text-red-600 hover:underline text-sm"
                        onClick={() => {
                          if (confirm(`Remove ${s.skill_name}?`)) deleteMut.mutate(s.id)
                        }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}

      {adding && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-lg shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-3">Add skill</h3>
            <FieldSet>
              <FieldGroup>
                <Field>
                  <FieldLabel>Skill</FieldLabel>
                  <SearchableSelect
                    value={draft.skill}
                    onChange={(v) => setDraft({ ...draft, skill: v })}
                    placeholder="Pick a skill…"
                    options={available.map((s) => ({ value: String(s.id), label: s.name }))}
                  />
                </Field>
                <Field>
                  <FieldLabel>Level</FieldLabel>
                  <Select value={draft.level} onValueChange={(v) => setDraft({ ...draft, level: v as any })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LEVEL_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field>
                  <FieldLabel>Years</FieldLabel>
                  <Input
                    type="number"
                    min={0} max={60}
                    value={draft.years_experience}
                    onChange={(e) => setDraft({ ...draft, years_experience: Number(e.target.value || 0) })}
                  />
                </Field>
                <Field>
                  <FieldLabel>Last used</FieldLabel>
                  <DatePicker
                    value={draft.last_used_date || ''}
                    onChange={(v) => setDraft({ ...draft, last_used_date: v })}
                  />
                </Field>
              </FieldGroup>
            </FieldSet>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
              <Button onClick={() => addMut.mutate()} disabled={!draft.skill || addMut.isPending}>
                {addMut.isPending ? 'Adding…' : 'Add'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────── Languages tab ───────────────

function LanguagesTab({ profile, qc }: { profile: Profile; qc: ReturnType<typeof useQueryClient> }) {
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState({ language_code: '', level: 'B2' })

  const addMut = useMutation({
    mutationFn: async () =>
      (await api.post('/consulting/profile-languages/', { profile: profile.id, ...draft })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'profile', profile.id] })
      setAdding(false)
      setDraft({ language_code: '', level: 'B2' })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const delMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/consulting/profile-languages/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'profile', profile.id] }),
  })

  return (
    <div>
      <div className="flex justify-end mb-3"><Button onClick={() => setAdding(true)}>+ Add language</Button></div>
      {profile.languages.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">No languages yet.</div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b"><tr>
              <th className="text-left px-4 py-2">Code</th>
              <th className="text-left px-4 py-2">Level</th>
              <th className="text-right px-4 py-2"></th>
            </tr></thead>
            <tbody className="divide-y">
              {profile.languages.map((l) => (
                <tr key={l.id}>
                  <td className="px-4 py-2 font-mono uppercase">{l.language_code}</td>
                  <td className="px-4 py-2">{l.level}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      className="text-red-600 hover:underline text-sm"
                      onClick={() => { if (confirm('Remove?')) delMut.mutate(l.id) }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-lg shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-3">Add language</h3>
            <FieldSet>
              <FieldGroup>
                <Field>
                  <FieldLabel>ISO code</FieldLabel>
                  <Input
                    value={draft.language_code}
                    onChange={(e) => setDraft({ ...draft, language_code: e.target.value.toLowerCase().slice(0, 8) })}
                    placeholder="en, fr, de…"
                    className="font-mono"
                  />
                </Field>
                <Field>
                  <FieldLabel>Level</FieldLabel>
                  <Select value={draft.level} onValueChange={(v) => setDraft({ ...draft, level: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANG_LEVELS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </FieldGroup>
            </FieldSet>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
              <Button onClick={() => addMut.mutate()} disabled={!draft.language_code || addMut.isPending}>
                {addMut.isPending ? 'Adding…' : 'Add'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────── Certifications tab ───────────────

function CertificationsTab({ profile, qc }: { profile: Profile; qc: ReturnType<typeof useQueryClient> }) {
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState({
    name: '', issuing_org: '', valid_from: '' as string | null, valid_until: '' as string | null,
  })

  const addMut = useMutation({
    mutationFn: async () =>
      (await api.post('/consulting/profile-certifications/', {
        profile: profile.id,
        name: draft.name,
        issuing_org: draft.issuing_org,
        valid_from: draft.valid_from || null,
        valid_until: draft.valid_until || null,
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'profile', profile.id] })
      setAdding(false)
      setDraft({ name: '', issuing_org: '', valid_from: '', valid_until: '' })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const delMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/consulting/profile-certifications/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'profile', profile.id] }),
  })

  return (
    <div>
      <div className="flex justify-end mb-3"><Button onClick={() => setAdding(true)}>+ Add certification</Button></div>
      {profile.certifications.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">No certifications yet.</div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b"><tr>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Issuer</th>
              <th className="text-left px-4 py-2">From</th>
              <th className="text-left px-4 py-2">Until</th>
              <th className="text-right px-4 py-2"></th>
            </tr></thead>
            <tbody className="divide-y">
              {profile.certifications.map((c) => (
                <tr key={c.id}>
                  <td className="px-4 py-2 font-medium">{c.name}</td>
                  <td className="px-4 py-2 text-muted-foreground">{c.issuing_org || <Dash />}</td>
                  <td className="px-4 py-2 text-muted-foreground">{c.valid_from || <Dash />}</td>
                  <td className="px-4 py-2 text-muted-foreground">{c.valid_until || <Dash />}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      className="text-red-600 hover:underline text-sm"
                      onClick={() => { if (confirm('Remove?')) delMut.mutate(c.id) }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-lg shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-3">Add certification</h3>
            <FieldSet>
              <FieldGroup>
                <Field>
                  <FieldLabel>Name</FieldLabel>
                  <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="PMP, AWS Solutions Architect…" />
                </Field>
                <Field>
                  <FieldLabel>Issuer</FieldLabel>
                  <Input value={draft.issuing_org} onChange={(e) => setDraft({ ...draft, issuing_org: e.target.value })} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field>
                    <FieldLabel>Valid from</FieldLabel>
                    <DatePicker value={draft.valid_from || ''} onChange={(v) => setDraft({ ...draft, valid_from: v })} />
                  </Field>
                  <Field>
                    <FieldLabel>Valid until</FieldLabel>
                    <DatePicker value={draft.valid_until || ''} onChange={(v) => setDraft({ ...draft, valid_until: v })} />
                  </Field>
                </div>
              </FieldGroup>
            </FieldSet>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
              <Button onClick={() => addMut.mutate()} disabled={!draft.name || addMut.isPending}>
                {addMut.isPending ? 'Adding…' : 'Add'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────── Layout helpers ───────────────

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">{children}</dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}
